import { FC, useContext, useEffect, useState } from 'react';
import { Switch } from '@material-tailwind/react';

import {
  Edition,
  EmailOption,
  LoadingOrigin,
  TeamMemberType,
} from '../../utils/enums';
import { UserInfo } from '../../utils/interfaces';
import { mailOption } from '../../utils/types';
// import { ScopedMutator } from 'swr/dist/types';
import { LoadingContext } from '../../utils/contexts/loadingContext';
import Toast from '../shared/toast';
import * as Sentry from '@sentry/nextjs';
import { useUser } from '@auth0/nextjs-auth0';

type Props = {
  userInfo: UserInfo;
  emailOptions?: mailOption[];
  mutateMailOptions: any;
};

const notificationOptionsTemplates = [
  {
    option: EmailOption.MondayMetrics,
    description: 'My Metrics (every Monday via email)',
    moreDetails: 'Info about how you are using Confirmed',
    permissions: [
      Edition.Premium,
      Edition.Professional,
      Edition.Team,
      Edition.Personal,
      Edition.MeetAndGreet,
      Edition.SuperConnector,
    ],
    teamAdminOnly: false,
  },
  {
    option: EmailOption.SundayMetrics,
    description: 'My Team Metrics (every Sunday via email)',
    moreDetails: 'Info about how your team is using Confirmed',
    permissions: [Edition.Team],
    teamAdminOnly: true,
  },
  {
    option: EmailOption.DailyReport,
    description: 'My Daily Snapshot (every day via email)',
    moreDetails: 'One stop shop for your meetings today',
    permissions: [
      Edition.Premium,
      Edition.Professional,
      Edition.Personal,
      Edition.Team,
      Edition.MeetAndGreet,
      Edition.SuperConnector,
    ],
    teamAdminOnly: false,
  },
  //Removed until actively being used
  // {
  //   option: EmailOption.ConfirmedTips,
  //   isEmailSelected: false,
  //   isTestSelected: false,
  //   description: 'Tips about Confirmed',
  //   moreDetails: '',
  //   permissions: [
  //     Edition.FreeTrial,
  //     Edition.Personal,
  //     Edition.Premium,
  //     Edition.Professional,
  //     Edition.Team,
  //   ],
  //   teamAdminOnly: false,
  // },
  // {
  //   option: EmailOption.PromotionalOffersFromConfirmed,
  //   isEmailSelected: false,
  //   isTestSelected: false,
  //   description: 'Promotional Offers From Confirmed',
  //   moreDetails: '',
  //   permissions: [
  //     Edition.FreeTrial,
  //     Edition.Personal,
  //     Edition.Premium,
  //     Edition.Professional,
  //     Edition.Team,
  //   ],
  //   teamAdminOnly: false,
  // },
  // {
  //   option: EmailOption.PromotionalOffersFromParters,
  //   isEmailSelected: false,
  //   isTestSelected: false,
  //   description: 'Promotional Offers From Confirmed Partners',
  //   moreDetails: '',
  //   permissions: [
  //     Edition.FreeTrial,
  //     Edition.Personal,
  //     Edition.Premium,
  //     Edition.Professional,
  //     Edition.Team,
  //   ],
  //   teamAdminOnly: false,
  // },
  {
    option: EmailOption.MeetAndGreetPreEventEmails,
    description: 'Meet & Greet Pre-Event Reminders via email',
    moreDetails: 'Reminders sent before Meet & Greet events',
    permissions: [
      Edition.Premium,
      Edition.Professional,
      Edition.Team,
      Edition.Personal,
      Edition.MeetAndGreet,
      Edition.SuperConnector,
    ],
    teamAdminOnly: false,
  },
  {
    option: EmailOption.MeetAndGreetPostEventEmails,
    description: 'Meet & Greet Post-Event Follow-Ups via email',
    moreDetails: 'Follow-up emails sent after Meet & Greet events',
    permissions: [
      Edition.Premium,
      Edition.Professional,
      Edition.Team,
      Edition.Personal,
      Edition.MeetAndGreet,
      Edition.SuperConnector,
    ],
    teamAdminOnly: false,
  },
];

const Notifications: FC<Props> = ({
  userInfo: initialUserInfo,
  emailOptions,
  mutateMailOptions,
}) => {
  const { user } = useUser();
  const { loading, setLoading } = useContext(LoadingContext);
  const [show, setShow] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string>('');
  const [userInfo, setUserInfo] = useState<UserInfo>(initialUserInfo);

  // Fetch admin account details if status is 500
  useEffect(() => {
    const fetchAdminAccountDetails = async () => {
      if ((user as any)?.accountDetails?.status === 500 && user?.sub) {
        try {
          const response = await fetch(
            `/api/admin/accountdetails/${user.sub}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
              },
            }
          );

          if (response.ok) {
            const adminAccountDetails = await response.json();
            // Update userInfo with admin account details
            setUserInfo((prev) => ({
              ...prev,
              accountDetails: adminAccountDetails,
            }));
          } else {
            const errorBody = await response.text();
            Sentry.captureMessage(
              `Failed to fetch admin account details: ${errorBody}`
            );
            console.error('Failed to fetch admin account details');
          }
        } catch (error) {
          Sentry.captureException(error);
          console.error('Error fetching admin account details:', error);
        }
      }
    };

    fetchAdminAccountDetails();
  }, [(user as any)?.accountDetails?.status, user?.sub]);

  const isTeamAdmin =
    userInfo?.accountDetails?.teamMemberType === TeamMemberType.administrator ||
    userInfo?.accountDetails?.teamMemberType === TeamMemberType.owner;

  const hasPermission = (
    editions: Edition[],
    requiresTeamAdmin: boolean
  ): boolean => {
    if (requiresTeamAdmin) return isTeamAdmin;
    return editions?.find(
      (edition) => edition === userInfo?.accountDetails?.edition
    ) === undefined
      ? false
      : true;
  };

  if (emailOptions === undefined || loading?.isloading)
    return (
      <div className="flex flex-col">
        <div className="flex flex-row p-4 w-full border-b border-gray-100 mb-2 opacity-50">
          <div className="w-4/6 font-bold text-gray-700">Notification Type</div>
          <div className="w-2/6 text-center font-bold text-gray-700">
            Preference
          </div>
        </div>
        {notificationOptionsTemplates.map((template) => {
          const isOptedOut =
            emailOptions?.find((mo) => mo.emailOption === template.option)
              ?.value ?? false;
          return (
            <div
              key={template.option}
              className={`flex flex-row items-center p-4 w-full border-b border-gray-50 opacity-50 ${
                hasPermission(template.permissions, template.teamAdminOnly)
                  ? ''
                  : 'hidden'
              }`}
            >
              <div className="w-4/6">
                <span className="font-medium text-gray-800">
                  {template.description}
                </span>
              </div>
              <div className="w-2/6 flex items-center justify-center gap-3">
                <span
                  className={`text-[10px] uppercase tracking-tighter ${
                    isOptedOut
                      ? 'text-confirmed-secondary font-bold'
                      : 'text-gray-400'
                  }`}
                >
                  Don't Send (opt-out)
                </span>
                <Switch
                  ripple={false}
                  disabled={true}
                  id={template.option.toString() + '_loading'}
                  checked={!isOptedOut}
                />
                <span
                  className={`text-[10px] uppercase tracking-tighter ${
                    !isOptedOut
                      ? 'text-confirmed-secondary font-bold'
                      : 'text-gray-400'
                  }`}
                >
                  Send it
                </span>
              </div>
            </div>
          );
        })}
      </div>
    );

  const updateMailOptions = async (mailOptions: mailOption[]) => {
    try {
      const result = await fetch('/api/user/saveMailOptions', {
        body: JSON.stringify(mailOptions),
        method: 'POST',
      });
      if (!result.ok) {
        const errorBody = await result.text();
        throw new Error(
          `API call failed with status ${result.status}: ${errorBody}`
        );
      }
    } catch (error) {
      Sentry.captureException(error);
      return error;
    }
  };

  const handleEmailOptionChange = async (
    option: EmailOption,
    checked: boolean
  ) => {
    if (emailOptions !== undefined) {
      // Optimistic update
      const opIndex = emailOptions?.findIndex(
        (mo) => mo.emailOption === option
      );

      const updatedOptions = [...emailOptions];
      updatedOptions[opIndex].value = checked;

      await mutateMailOptions(
        '/api/user/getMailOptions',
        updateMailOptions(updatedOptions)
      ).then(() => {
        setAlertMessage('Notification Preferences Saved');
        setShow(true);
      });
    }
  };

  return (
    <div className="flex flex-col">
      <Toast
        show={show}
        setShow={setShow}
        alertMessage={alertMessage}
        fixedPosition={true}
      />

      <div className="flex flex-row p-4 w-full border-b border-gray-100 mb-2">
        <div className="w-4/6 font-bold text-gray-700">Notification Type</div>
        <div className="w-2/6 text-center font-bold text-gray-700">
          Preference
        </div>
      </div>
      {notificationOptionsTemplates.map((template) => {
        const isOptedOut =
          emailOptions?.find((mo) => mo.emailOption === template.option)
            ?.value ?? false;

        return (
          <div
            key={template.option}
            className={`flex flex-row items-center p-4 w-full border-b border-gray-50 hover:bg-gray-50/50 transition-colors ${
              hasPermission(template.permissions, template.teamAdminOnly)
                ? ''
                : 'hidden'
            }`}
          >
            <div className="w-4/6">
              <span className="font-medium text-gray-800">
                {template.description}
              </span>
              {template.moreDetails && (
                <p className="text-xs text-gray-500 mt-1">
                  {template.moreDetails}
                </p>
              )}
            </div>
            <div className="w-2/6 flex items-center justify-center gap-3">
              <span
                className={`text-[10px] uppercase tracking-tighter ${
                  isOptedOut
                    ? 'text-confirmed-secondary font-bold'
                    : 'text-gray-400'
                }`}
              >
                Don't Send (opt-out)
              </span>
              <Switch
                ripple={false}
                className="checked:bg-confirmed-secondary"
                containerProps={{
                  className: 'p-0',
                }}
                color="teal"
                id={template.option.toString()}
                checked={!isOptedOut}
                onChange={(e) =>
                  handleEmailOptionChange(template.option, !e.target.checked)
                }
              />
              <span
                className={`text-[10px] uppercase tracking-tighter ${
                  !isOptedOut
                    ? 'text-confirmed-secondary font-bold'
                    : 'text-gray-400'
                }`}
              >
                Send it
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default Notifications;
